import { FormEvent, useState } from "react";
import AdminResourceForm, { AdminField } from "../../components/admin/AdminResourceForm";
import ServiceImageUpload from "../../components/admin/ServiceImageUpload";
import { useAdminData } from "../../admin/AdminDataContext";
import { Service } from "../../types";

const emptyService = {
  name: "",
  description: "",
  priceRange: "",
};

function serviceToFields(service: typeof emptyService): AdminField[] {
  return [
    { name: "name", label: "Name", value: service.name, required: true },
    { name: "description", label: "Description", value: service.description, required: true },
    { name: "priceRange", label: "Price range", value: service.priceRange },
  ];
}

function buildServicePayload(fields: AdminField[], imageUrl: string): Omit<Service, "id"> {
  const map = Object.fromEntries(fields.map((f) => [f.name, f.value]));
  return {
    name: map.name,
    description: map.description,
    priceRange: map.priceRange || undefined,
    imageUrl: imageUrl || undefined,
  };
}

function AdminServices() {
  const { services, loading, error, addService, updateService, removeService } = useAdminData();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fields, setFields] = useState<AdminField[]>(serviceToFields(emptyService));
  const [imageUrl, setImageUrl] = useState("");

  const resetImage = (service?: Service) => {
    setImageUrl(service?.imageUrl ?? "");
  };

  const openCreate = () => {
    setEditingId(null);
    setFields(serviceToFields(emptyService));
    resetImage();
    setShowForm(true);
  };

  const openEdit = (service: Service) => {
    setEditingId(service.id);
    setFields(
      serviceToFields({
        name: service.name,
        description: service.description,
        priceRange: service.priceRange ?? "",
      })
    );
    resetImage(service);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleChange = (name: string, value: string) => {
    setFields((prev) => prev.map((f) => (f.name === name ? { ...f, value } : f)));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = buildServicePayload(fields, imageUrl);
    if (!payload.name || !payload.description) return;
    try {
      if (editingId) {
        await updateService(editingId, payload);
      } else {
        await addService(payload);
      }
      closeForm();
    } catch {
      // mutation error surfaced via AdminDataContext if needed
    }
  };

  const handleDelete = async (service: Service) => {
    if (window.confirm(`Delete "${service.name}"?`)) {
      await removeService(service.id);
    }
  };

  if (loading) return <p className="body-text">Loading services...</p>;
  if (error) return <p className="admin-error">Error: {error}</p>;

  return (
    <div className="admin-card">
      <div className="admin-card__header">
        <h1 className="h1-text">Services</h1>
        {!showForm && (
          <button type="button" className="admin-btn admin-btn--primary" onClick={openCreate}>
            Add service
          </button>
        )}
      </div>

      {showForm && (
        <>
          <AdminResourceForm
            title={editingId ? "Edit service" : "Add service"}
            fields={fields}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onCancel={closeForm}
            submitLabel={editingId ? "Save changes" : "Add service"}
          />
          <ServiceImageUpload imageUrl={imageUrl} onImageChange={setImageUrl} />
        </>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Price range</th>
            <th>Photo</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {services.map((service) => (
            <tr key={service.id}>
              <td>{service.name}</td>
              <td>{service.priceRange ?? "—"}</td>
              <td>{service.imageUrl ? "Yes" : "—"}</td>
              <td>
                <div className="admin-table-actions">
                  <button type="button" className="admin-btn" onClick={() => openEdit(service)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn--danger"
                    onClick={() => handleDelete(service)}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminServices;
